import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { compact } from 'lodash-es';
import { Typography } from 'seagull';
import { isFutureEffectiveDate } from '~/business-logic/billingLogic';
import { BillingSettingsSection } from '~/components/ServiceDetails/BillingSettingsSection/BillingSettingsSection';
import { serviceBillingModel, serviceCostModel, serviceModel, timeModel } from '~/models';
import { PreapprovalPeriodType, RecurrencePeriodTypes } from '~/models/service-billing';
import { formatDateLong } from '~/models/time';
import { formatLongMonthUSDate } from '~/utils';
import { utils } from '..';
import pluralize from '../pluralize';
const onDemandPreapprovalPeriodCopyMap = {
    [PreapprovalPeriodType.None]: {
        period: null,
        verb: 'requires'
    },
    [PreapprovalPeriodType.NoTimeLimit]: {
        period: null,
        verb: 'requires'
    },
    [PreapprovalPeriodType.Weekly]: {
        period: 'week',
        verb: 'requires'
    },
    [PreapprovalPeriodType.BiWeekly]: {
        period: 'two weeks period',
        verb: 'will require'
    },
    [PreapprovalPeriodType.Monthly]: {
        period: 'month',
        verb: 'requires'
    },
    [PreapprovalPeriodType.Quarterly]: {
        period: 'quarter',
        verb: 'requires'
    },
    [PreapprovalPeriodType.Yearly]: {
        period: 'year',
        verb: 'requires'
    }
};
function wasAgreementApprovedWithFutureEffectiveDate(params) {
    const { agreementData } = params;
    return agreementData.daysToEffectiveDate > 0;
}
function costDetails(param) {
    let { service } = param;
    const { cost, billing } = service;
    if (serviceCostModel.isVariablePrice(cost) && cost.unitCap) {
        if (cost.unitCap.isZero()) {
            return 'Charging this service will require approval';
        } else {
            const unitText = pluralize(serviceCostModel.formattedUnitName(cost), cost.unitCap.toNumber(), true);
            if (serviceBillingModel.isRecurring(billing)) {
                const recurrenceTextMap = {
                    [RecurrencePeriodTypes.Weekly]: 'week',
                    [RecurrencePeriodTypes.BiWeekly]: 'two weeks',
                    [RecurrencePeriodTypes.Monthly]: 'month',
                    [RecurrencePeriodTypes.Quarterly]: 'quarter',
                    [RecurrencePeriodTypes.Yearly]: 'year'
                };
                const recurrenceText = recurrenceTextMap[billing.recurrencePeriod];
                return "Charging more than ".concat(unitText, " per ").concat(recurrenceText, " for this service will require approval");
            } else if (serviceBillingModel.isOnDemand(billing)) {
                const copy = onDemandPreapprovalPeriodCopyMap[billing.preapprovalPeriod];
                const periodSuffix = copy.period ? " per ".concat(copy.period) : '';
                return "Charging more than ".concat(unitText).concat(periodSuffix, " for this service ").concat(copy.verb, " client approval");
            } else {
                return "Charging more than ".concat(unitText, " for this service will require approval");
            }
        }
    } else if (serviceCostModel.isRangePrice(cost)) {
        const maxPrice = utils.formatDollarAmount(serviceCostModel.applyDiscount(cost));
        return "Charging this service over ".concat(maxPrice, " will require approval");
    } else {
        return null;
    }
}
function billingSettingsSection(param) {
    let { agreementData, isReadOnly, testId, service, skin, variant, weight, relationshipState, onBillUpfrontModeChange = ()=>{}, onDayOfMonthChange = ()=>{}, onDayOfWeekChange = ()=>{}, onDayOfYearChange = ()=>{} } = param;
    if (!serviceBillingModel.isOngoing(service.billing)) return null;
    return /*#__PURE__*/ _jsx(BillingSettingsSection, {
        service: service,
        testId: testId || '',
        isReadOnly: isReadOnly,
        isActiveAgreement: agreementData.isActiveAgreement(),
        skin: skin,
        variant: variant,
        weight: weight,
        relationshipState: relationshipState,
        onDayOfMonthChange: onDayOfMonthChange,
        onDayOfWeekChange: onDayOfWeekChange,
        onBillUpfrontModeChange: onBillUpfrontModeChange,
        onDayOfYearChange: onDayOfYearChange
    });
}
function getSuffixForAutomaticBilling(params) {
    return isFutureEffectiveDate(params.agreementData.effectiveDate, params.now) || wasAgreementApprovedWithFutureEffectiveDate(params) ? 'on the effective date of the agreement' : 'on proposal acceptance';
}
function billingDetails(params) {
    const { service, featureFlags, skin, variant, weight, testId } = params;
    const { billing, cost } = service;
    return serviceBillingModel.accept(billing, {
        visitOnApproval: ()=>{
            if (serviceModel.isFromAmendment(service)) return null;
            return "Billing is done once ".concat(getSuffixForAutomaticBilling(params));
        },
        visitOneTimeManual: ()=>'Can be billed once',
        visitOneTimeInStages: (billing)=>{
            const stages = billing.oneTimeInStages.stagesWithFinalAmount(cost);
            const amounts = stages.map((s)=>utils.formatDollarAmount(s.discountedFinalAmount)).join(', ');
            const pluralStages = pluralize('stage', stages.length, true);
            const autoInvoiceIssued = ()=>isFutureEffectiveDate(params.agreementData.effectiveDate, params.now) || wasAgreementApprovedWithFutureEffectiveDate(params) ? 'on the effective date of the agreement' : 'upon agreement approval';
            const terms = billing.oneTimeInStages.isAutoFirst() ? "The first invoice will be issued ".concat(autoInvoiceIssued(), " and the remaining invoices will be issued according to the service terms") : "Invoices will be issued according to the service terms";
            return "Billed in ".concat(pluralStages, " (").concat(amounts, "). ").concat(terms, ".");
        },
        visitRepeatableManual: (billing)=>{
            if (serviceCostModel.isVariablePrice(cost)) return null;
            const recurrenceTextMap = {
                [RecurrencePeriodTypes.Weekly]: 'per week',
                [RecurrencePeriodTypes.BiWeekly]: 'every two weeks',
                [RecurrencePeriodTypes.Monthly]: 'per month',
                [RecurrencePeriodTypes.Quarterly]: 'per quarter',
                [RecurrencePeriodTypes.Yearly]: 'per year'
            };
            const recurrenceText = recurrenceTextMap[billing.recurrencePeriod];
            return "This service can be billed once ".concat(recurrenceText);
        },
        visitOngoing: (billing)=>{
            if (featureFlags.START_BILLING_AT || featureFlags.START_BILLING_AT_PHASE_2_INLINE) {
                const startBillingAt = serviceModel.startBillingAt(service);
                if (startBillingAt) {
                    return /*#__PURE__*/ _jsxs(Typography, {
                        noWrap: true,
                        skin: skin,
                        variant: variant,
                        weight: weight,
                        testId: "".concat(testId || '', "-effective-date-message"),
                        children: [
                            "Billing starts on ",
                            formatLongMonthUSDate(startBillingAt),
                            "."
                        ]
                    });
                }
                if (serviceModel.isFromAmendment(service)) {
                    return null;
                } else if (serviceModel.isBillOnPeriodBeforeNextBilling(service)) {
                    return "This service will be billed ".concat(getSuffixForAutomaticBilling(params));
                }
                return null;
            }
            if (serviceModel.isFromAmendment(service)) {
                return null;
            } else if (billing.isBilledUpfront && !serviceBillingModel.isSkipBillingOnAcceptance(billing)) {
                return "This service will be billed ".concat(getSuffixForAutomaticBilling(params));
            } else {
                return null;
            }
        },
        visitOnDemand: ()=>'This service can be billed on demand'
    });
}
function prorated(param) {
    let { service } = param;
    const { billing } = service;
    if (serviceBillingModel.isOngoing(billing) && billing.isProrated) return 'The first payment for this service is prorated';
    return null;
}
function limitedTimes(param) {
    let { service } = param;
    const { billing } = service;
    if ((serviceBillingModel.isOngoing(billing) || serviceBillingModel.isOnDemand(billing)) && serviceModel.maxChargesEnabled(service)) return "This service can be billed ".concat(serviceBillingModel.formatMaxChargesLimit(billing));
    return null;
}
function paused(param) {
    let { service, agreementData } = param;
    const { billing } = service;
    if (!agreementData.isActiveAgreement() && serviceBillingModel.isOngoing(billing) && billing.isPaused) return 'Billing for this service will commence only after it has been activated by the service provider';
    return null;
}
function priceIncrease(param) {
    let { service } = param;
    const { cost } = service;
    const priceIncrease = serviceCostModel.priceIncrease(cost);
    if (priceIncrease.exists()) {
        const increaseOn = priceIncrease.increaseOn();
        const suffix = increaseOn ? "starting on ".concat(timeModel.formatDateLong(increaseOn)) : 'on agreement anniversary';
        const increaseBy = priceIncrease.isNonPositiveIncrease() ? 'x' : "".concat(priceIncrease.percentIncrease(), "%");
        return "Price increases by ".concat(increaseBy, " every year ").concat(suffix);
    }
    return null;
}
function discountPeriodExpiration(params) {
    const { service } = params;
    const discount = serviceModel.discount(service) || serviceModel.expiredDiscount(service);
    if (!discount) return null;
    const discountPeriodExpiration = serviceCostModel.discountPeriodExpiration({
        discount
    });
    if (!discountPeriodExpiration) return null;
    const date = discountPeriodExpiration.date();
    if (date) return "The discount for this service expires on ".concat(formatDateLong(date));
    const periodType = discountPeriodExpiration.period();
    const amount = discountPeriodExpiration.amount();
    if (!periodType || !amount) return null;
    return "The discount for this service expires ".concat(amount, " ").concat(pluralize(periodType, amount), " after the agreement effective date");
}
export function getServiceBulletPoints(params) {
    return {
        bullets: compact([
            billingSettingsSection(params),
            billingDetails(params),
            costDetails(params),
            prorated(params),
            limitedTimes(params),
            priceIncrease(params),
            discountPeriodExpiration(params)
        ]),
        pausedCallout: paused(params)
    };
}
